import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import { BASE_IMG_URL } from '@images/imageUrls';
import { px2rem } from '@utils/px2rem';
import classNames from 'classnames';
import FeedbackList from '@components/feedbackList';

interface FeedbackListSearchH5PageProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteAction;
    user?: UserAction;
    thread?: ThreadAction;
    feedbackList?: any[];
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    searchInputValue?: string;
    searchKeyword?: string;
    hasSearched?: boolean;
    onSearchChange?: (value: string) => void;
    onSearchConfirm?: () => void;
}

interface FeedbackListSearchH5State {
    loading: boolean;
    error: any;
    pageLoaded: boolean;
    headerShowStatus: boolean;
}

@inject('app', 'site', 'user', 'thread')
@observer
class FeedbackListSearchH5Page extends BasePage<FeedbackListSearchH5PageProps, FeedbackListSearchH5State> {
    searchInputRef = React.createRef<HTMLInputElement>();

    state: FeedbackListSearchH5State = {
        loading: false,
        error: null,
        pageLoaded: false,
        headerShowStatus: true,
    };

    componentDidMount() {
        const { site } = this.props;
        if (site) {
            this.setState({ headerShowStatus: site.getShowHeader() }, () => {
                site.setShowHeader(false);
            });
        }
        document.title = '搜索问题';
        this.searchInputRef.current?.focus();
        super.componentDidMount();
    }

    componentWillUnmount() {
        const { site } = this.props;
        const { headerShowStatus } = this.state;
        site?.setShowHeader(headerShowStatus);
        document.title = '';
    }

    handleSearchBack = () => {
        this.props.history.goBack();
    };

    handleSearchClear = () => {
        this.props.onSearchChange?.('');
        this.searchInputRef.current?.focus();
    };

    handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            this.props.onSearchConfirm?.();
        }
    };

    renderSearchBar = () => {
        const { searchInputValue = '', onSearchChange, onSearchConfirm } = this.props;
        const canSearch = !!searchInputValue.trim();

        return (
            <div className={styles.searchHeader}>
                <div className={styles.searchBox}>
                    <img
                        src={`${BASE_IMG_URL}/feedback/icon2.png`}
                        alt="search"
                        className={styles.searchIcon}
                    />
                    <input
                        ref={this.searchInputRef}
                        type="text"
                        className={styles.searchInput}
                        placeholder="搜索问题"
                        value={searchInputValue}
                        onChange={(e) => onSearchChange?.(e.target.value)}
                        onKeyDown={this.handleSearchKeyDown}
                    />
                    {!!searchInputValue && (
                        <button
                            type="button"
                            className={styles.clearBtn}
                            onClick={this.handleSearchClear}
                            aria-label="清空"
                        />
                    )}
                </div>

                <button
                    type="button"
                    className={classNames(styles.searchBtn, canSearch && styles.searchBtnActive)}
                    onClick={() => onSearchConfirm?.()}
                    disabled={!canSearch}
                >
                    搜索
                </button>
            </div>
        );
    };

    renderFeedbackList = () => {
        const { feedbackList, loading, hasMore, onLoadMore, searchKeyword } = this.props;
        return (
            <div style={{ marginBottom: 16 }}>
                <FeedbackList
                    data={feedbackList}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={onLoadMore}
                    searchKeyword={searchKeyword}
                    namespace="feedbackListSearch"
                    emptyIcon={`${BASE_IMG_URL}/common/null4.png`}
                    emptyText='未搜索到相关内容'
                />
            </div>
        );
    };

    render() {
        const { site, hasSearched } = this.props;
        const downloadAppVisible = site?.appDownloadShowStatus;

        return (
            <div
                className={styles.wrapper}
                style={downloadAppVisible ? { paddingTop: px2rem(88) } : undefined}
            >
                <div className={styles.feedbackContent}>
                    {this.renderSearchBar()}
                    {hasSearched && this.renderFeedbackList()}
                </div>
            </div>
        );
    }
}

export default withRouter(FeedbackListSearchH5Page);
